// ============================================================================
// KLINISOL CRO - Services Page (REDESIGNED)
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { 
    FileText, 
    Hospital, 
    BarChart3, 
    CheckCircle, 
    Shield, 
    AlertTriangle,
    ArrowRight
} from 'lucide-react';

const Services = () => {
    const services = [
        {
            icon: FileText,
            title: "Protocol Development & Regulatory Strategy",
            lead: "Expert protocol design and regulatory planning for successful investigator-initiated trials",
            items: [
                "Protocol writing and optimization",
                "Statistical analysis plan development",
                "IND/IDE strategy and preparation",
                "IRB submission package preparation",
                "Protocol amendments and modifications",
                "Clinical trial feasibility assessment"
            ]
        },
        {
            icon: Hospital,
            title: "Site Management & Monitoring",
            lead: "End-to-end site management from selection through closeout",
            items: [
                "Site identification and qualification",
                "Site initiation visits (SIV)",
                "On-site and remote monitoring",
                "Source data verification (SDV)",
                "Risk-based monitoring strategies",
                "Site closeout and archiving"
            ]
        },
        {
            icon: BarChart3,
            title: "Data Management & Biostatistics",
            lead: "FDA 21 CFR Part 11 compliant data management and analysis",
            items: [
                "eCRF design and database build",
                "Data entry and validation",
                "Medical coding (MedDRA, WHODrug)",
                "Data cleaning and query management",
                "CDISC SDTM and ADaM datasets",
                "Statistical analysis and reporting"
            ]
        },
        {
            icon: CheckCircle,
            title: "Regulatory Affairs & Compliance",
            lead: "Expert regulatory guidance and submissions management",
            items: [
                "IND/IDE application preparation",
                "Annual IND reports and amendments",
                "IRB submissions and correspondence",
                "FDA meeting requests and briefing packages",
                "ClinicalTrials.gov registration",
                "Regulatory inspection readiness"
            ]
        },
        {
            icon: Shield,
            title: "Quality Assurance & Auditing",
            lead: "GCP-compliant quality oversight and inspection preparedness",
            items: [
                "Quality management system (QMS)",
                "Internal audits and quality reviews",
                "Sponsor audit preparation",
                "FDA inspection readiness",
                "CAPA (Corrective and Preventive Action)",
                "SOP development and management"
            ]
        },
        {
            icon: AlertTriangle,
            title: "Safety & Pharmacovigilance",
            lead: "Comprehensive safety monitoring and adverse event management",
            items: [
                "AE/SAE reporting and reconciliation",
                "Expedited safety reports (IND Safety Reports)",
                "Safety database management",
                "DSMB support and reporting",
                "Annual safety reports (DSUR)",
                "Safety signal detection and analysis"
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Page Header */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-16 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        Comprehensive CRO support for investigator-initiated clinical trials
                    </p>
                </div>
            </section>

            {/* Services Detail */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto space-y-12">
                    {services.map((service, index) => (
                        <div 
                            key={index}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-lg hover:border-[#E91E63] transition-all"
                        >
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Icon */}
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-xl flex items-center justify-center">
                                        <service.icon className="w-10 h-10 text-[#E91E63]" />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                        {service.title}
                                    </h2>
                                    <p className="text-lg text-gray-700 mb-4">
                                        {service.lead}
                                    </p>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {service.items.map((item, itemIndex) => (
                                            <li 
                                                key={itemIndex}
                                                className="flex items-start gap-2 text-gray-600"
                                            >
                                                <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-6 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Let's Discuss Your Clinical Trial Needs
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Contact us for a consultation on how we can support your investigator-initiated trial
                    </p>
                    <Link 
                        to="/contact" 
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#475569] text-[#475569] font-bold rounded-lg hover:border-[#E91E63] hover:shadow-xl transition-all text-lg group"
                    >
                        <span>Contact Us</span>
                        <ArrowRight className="w-6 h-6 group-hover:text-[#E91E63] transition-colors" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-2">
                                KLIN<span className="text-[#E91E63]">i</span>SOL
                            </h3>
                            <p className="text-sm opacity-90">Advancing Investigator-Initiated Research</p>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/" className="hover:text-[#E91E63] transition-colors">Home</Link></li>
                                <li><Link to="/services" className="hover:text-[#E91E63] transition-colors">Services</Link></li>
                                <li><Link to="/therapeutic-areas" className="hover:text-[#E91E63] transition-colors">Therapeutic Areas</Link></li>
                                <li><Link to="/about" className="hover:text-[#E91E63] transition-colors">About</Link></li>
                                <li><Link to="/contact" className="hover:text-[#E91E63] transition-colors">Contact</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Contact</h4>
                            <p className="text-sm opacity-90 mb-1">600 Broadway Ste 200 #2189</p>
                            <p className="text-sm opacity-90 mb-2">Albany, NY 12207</p>
                            <p className="text-sm opacity-90 mb-1">Email: info@klinisol.com</p>
                            <p className="text-sm opacity-90">Website: klinisol.com</p>
                        </div>
                    </div>
                    
                    <div className="border-t border-white/20 pt-6 text-center">
                        <p className="text-sm opacity-90 mb-4">&copy; 2025 KLINISOL. All rights reserved.</p>
                        
                        {/* Social Media Links */}
                        <div className="flex items-center justify-center gap-4">
                            <a 
                                href="https://www.linkedin.com/company/klinisol/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://youtube.com/@klinisol721" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="YouTube"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Services;